import React, { useState } from 'react';
import { LogoImage } from '../ui/LogoImage';

interface AuthFormProps {
  mode: 'login' | 'register';
  onSubmit: (email: string, password: string, name?: string, inviteCode?: string) => void;
  loading: boolean;
  onModeChange?: (mode: 'login' | 'register') => void;
  errorMessage?: string;
}

export function AuthForm({ mode, onSubmit, loading, onModeChange, errorMessage }: AuthFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(email, password, name, inviteCode);
  };

  return (
    <div className="min-h-screen bg-paper-cream flex items-center justify-center p-4">
      <div className="retro-card p-8 max-w-md w-full">
        {/* Logo and Branding */}
        <div className="text-center mb-8">
          <LogoImage size="lg" className="mb-4" />
          <h2 className="text-2xl font-bold text-text-dark mb-2">
            {mode === 'login' ? 'Welcome Back' : 'Join MacTrack'}
          </h2>
          <p className="text-text-medium text-sm">
            {mode === 'login' 
              ? 'Sign in to access your client dashboard' 
              : 'Create your account to start managing clients'
            }
          </p>
        </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'register' && (
          <>
            <div>
              <label 
                htmlFor="name" 
                className="block text-sm font-medium text-text-medium mb-1"
              >
                Full Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="vintage-input w-full"
                required
                placeholder="Enter your full name"
                aria-describedby="name-help"
              />
              <p id="name-help" className="text-xs text-text-medium mt-1">
                This will be displayed to your clients
              </p>
            </div>
            
            <div>
              <label 
                htmlFor="inviteCode" 
                className="block text-sm font-medium text-text-medium mb-1"
              >
                Beta Invite Code
              </label>
              <input
                id="inviteCode"
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                className="vintage-input w-full"
                required
                placeholder="Enter your beta invite code"
                aria-describedby="invite-help"
                maxLength={20}
              />
              <p id="invite-help" className="text-xs text-text-medium mt-1">
                Required for beta access. Contact support if you need a code.
              </p>
            </div>
          </>
        )}
        
        <div>
          <label 
            htmlFor="email" 
            className="block text-sm font-medium text-text-medium mb-1"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="vintage-input w-full"
            required
            placeholder="Enter your email"
            aria-describedby="email-help"
          />
          <p id="email-help" className="text-xs text-text-medium mt-1">
            We'll never share your email with anyone else
          </p>
        </div>
        
        <div>
          <label 
            htmlFor="password" 
            className="block text-sm font-medium text-text-medium mb-1"
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="vintage-input w-full"
            required
            placeholder="Enter your password"
            minLength={8}
            aria-describedby="password-help"
          />
          <p id="password-help" className="text-xs text-text-medium mt-1">
            Must be at least 8 characters long
          </p>
        </div>
        
        <button
          type="submit"
          disabled={loading}
          className="neon-button w-full py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          aria-describedby="submit-help"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="loading mr-2"></div>
              Processing...
            </div>
          ) : (
            mode === 'login' ? 'Sign In' : 'Create Account'
          )}
        </button>
        
        <p id="submit-help" className="text-xs text-text-medium text-center">
          {loading ? 'Please wait while we process your request...' : ''}
        </p>
        
        {/* Error Message Display */}
        {errorMessage && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <span className="text-red-600 font-semibold text-sm">ERROR</span>
              <span className="text-red-800 text-sm">{errorMessage}</span>
            </div>
          </div>
        )}
        
        {/* Mode Toggle */}
        {onModeChange && (
          <div className="mt-6 text-center">
            <p className="text-sm text-text-medium mb-2">
              {mode === 'login' ? "Don't have an account?" : "Already have an account?"}
            </p>
            <button
              type="button"
              onClick={() => onModeChange(mode === 'login' ? 'register' : 'login')}
              className="text-neon-blue hover:text-blue-600 font-medium text-sm transition-colors"
            >
              {mode === 'login' ? 'Create Account' : 'Sign In'}
            </button>
          </div>
        )}
        
        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-border-light">
          <p className="text-xs text-text-light text-center">
            Professional nutrition tracking and client management
          </p>
        </div>
      </form>
    </div>
  </div>
  );
}